import React, { useMemo, useState, useCallback } from "react";
import { Table as RBTable } from "react-bootstrap";
import { useTable } from "react-table";
import { DndProvider } from "react-dnd";
import { useMutation } from "@tanstack/react-query";
import { changeFoodOrder } from "Services/Food";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { TableProps } from "./Table.props";
import DraggableTableRow from "./DraggableTableRow";
import "./style.scss";

const borderStyle = {
    border: "1px solid gray",
    padding: "8px 10px",
};

const Table = React.memo<TableProps>(
    ({
         columns,
         data,
         onRowClick,
         onRightClick,
         refetch,
         withDnd,
         params,
         className = "",
         withExtendRow,
         hover = true,
         newTabLink,
         withRightClick = false,
         ...props
     }) => {
        const tColumns = useMemo(() => columns, [columns]);
        const tData = useMemo(() => data, [data]);

        const [tableData, setTableData] = useState(tData);

        const getRowId = useCallback((row: any) => {
            return (
                row.id ||
                row.good_id ||
                row.food_id ||
                row.row_id ||
                row.uid ||
                row.financial_id ||
                row.snappfood_order_id ||
                row.branch_id
            );
        }, []);

        const { mutate: changeOrderMutate, isLoading } = useMutation(
            changeFoodOrder,
            {
                onSuccess: () => {
                    if (refetch) {
                        refetch();
                    }
                },
            }
        );

        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            rows,
            prepareRow,
        } = useTable({
            data: tData,
            columns: tColumns,
            getRowId,
        });

        const moveRow = useCallback(
            (dragIndex: number, hoverIndex: number) => {
                const dragCard = tableData[dragIndex];
                setTableData(
                    update(tableData, {
                        $splice: [
                            [dragIndex, 1],
                            [hoverIndex, 0, dragCard],
                        ],
                    })
                );
            },
            [tableData]
        );

        const dropRow = (dropItem: any, vendor_id: number) => {
            const data = {
                id: dropItem.id,
                vendor_id: vendor_id,
                old_sort: dropItem.sort,
                new_sort: tData[dropItem.index]?.sort,
            };
            if (data.old_sort !== data.new_sort && !isLoading) {
                changeOrderMutate({
                    params: params,
                    body: data,
                });
            }
        };

        return (
            <DndProvider backend={HTML5Backend}>
                <RBTable
                    {...props}
                    {...getTableProps()}
                    bordered
                    hover={hover}
                    className={`kitchen-list-container kitchen-waste-list-container rtl ${className}`}
                >
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                                <th {...column.getHeaderProps()}>
                                    {column.render("Header")}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                    {withExtendRow
                        ? rows.map((row) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td
                                            style={borderStyle}
                                            {...cell.getCellProps()}
                                        >
                                            {cell.render("Cell")}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })
                        : !withDnd
                            ? rows.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr
                                        id={
                                            row.original.id ||
                                            row.original.good_id ||
                                            row.original.food_id ||
                                            row.original.financial_id ||
                                            row.original.uid
                                        }
                                        {...row.getRowProps()}
                                        onClick={() => {
                                            if (onRowClick) {
                                                onRowClick(row.original);
                                            }
                                        }}
                                    >
                                        {row.cells.map((cell) => (
                                            <>
                                                {withRightClick ? (
                                                    <td
                                                        className="new-tab-link-container"
                                                        {...cell.getCellProps()}
                                                    >
                                                        <a
                                                            href={
                                                                newTabLink ||
                                                                "#"
                                                            }
                                                            className="new-tab-link-style"
                                                            onContextMenu={() => {
                                                                //@ts-ignore
                                                                onRightClick(
                                                                    row.original
                                                                );
                                                            }}
                                                        >
                                                            {cell.render(
                                                                "Cell"
                                                            )}
                                                        </a>
                                                    </td>
                                                ) : (
                                                    <td
                                                        {...cell.getCellProps()}
                                                    >
                                                        {cell.render(
                                                            "Cell"
                                                        )}
                                                    </td>
                                                )}
                                            </>
                                        ))}
                                    </tr>
                                );
                            })
                            : rows.map((row, index) => {
                                prepareRow(row);
                                return (
                                    <DraggableTableRow
                                        key={row.id}
                                        id={row.id}
                                        index={index}
                                        row={row}
                                        moveRow={moveRow}
                                        dropRow={({ ...args }: any) =>
                                            dropRow(args, row.original.vendor_id)
                                        }
                                    />
                                );
                            })}
                    </tbody>
                </RBTable>
            </DndProvider>
        );
    }
);

export default Table;